import axios from '@/plugins/axios';
import { get, post } from '@/helpers/requests';
import router from '@/router/';
import i18n from '@/plugins/i18n';
import handlerRequestPassword from '@/server/authentication';

/**
 * [action]
 * Processa o login
 * Realiza o pedido a API com as informações inseridas no formulário de login ( email, password )
 *
 * @param {Object} ctx
 * @param {Object} ctx.storePayload
 * @param {Object} ctx.state
 * @param {Function} ctx.dispatch
 * @param {Function} ctx.commit
 * @since 1.1.0.
 * @category action
 * @author Tiago Martinho
 *
 */
const processLogin = async ({
    rootState: { storePayload },
    dispatch,
    commit
}) => {
    const formData = storePayload.payload;

    // valida se os dois campos foram preenchidos. ( email, password )
    // caso tenham sido, é submitido um pedido de API, de forma a validar se tem acesso ao software
    //     caso tenha acesso ao software, é guardado a sua informação em store e redirecionado para a home
    //                                    ou para a página que tentou aceder
    //     caso não tenha, é apresentado uma mensagem de erro ao utilizador.
    // case não tenha sido, é apresentado uma mensagem de erro ao utilizador.
    if ( Object.keys( formData ).length === 2 ) {
        commit( 'TOGGLE_LOADING', true );
        await post( '/auth/login', formData )
            .then( ({ data: { content: { data } } }) => {
                dispatch( 'registUser', data );
            })
            .catch( () => {
                commit( 'MANAGE_RESPONSE', {
                    has: true,
                    type: 'insuccess',
                    message: i18n.t( 'error.authentication' )
                });
            });
        commit( 'TOGGLE_LOADING', false );
    } else {
        const missingFields = {
            ...( !formData.email && { email: true }),
            ...( !formData.password && { password: true })
        };

        commit( 'MANAGE_MISSING_FIELDS', missingFields );
    }
};

/**
 * [action]
 * Processa o pedido de reset password
 *
 * @param {Object} ctx
 * @param {Object} ctx.storePayload
 * @param {Object} ctx.state
 * @param {Function} ctx.commit
 * @since 1.1.0.
 * @category action
 * @author Tiago Martinho
 *
 */
const requestResetPassword = async ({ rootState: { storePayload: { payload } }, commit, dispatch }) => {
    // activação do loading
    commit( 'TOGGLE_LOADING', true );

    // pedido
    const response = await get( '/auth/reset-password', { ...payload })
        .then( ({ data }) => ({
            has: true,
            type: 'success',
            message: data.message,
            content: data.content.data
        }) )
        .catch( error => ({
            has: true,
            type: 'insuccess',
            message: error.message,
            errors: error.content.errors
        }) );

    // procedimento no final da submissão
    dispatch( 'finalSubmission', response );
};

/**
 * [action]
 * Processa o reset password
 * Realiza o pedido a API com as informações inseridas no formulário de reset password ( email, password & confirmação password )
 *
 * @param {Object} ctx
 * @param {Object} ctx.storePayload
 * @param {Object} ctx.state
 * @param {Function} ctx.dispatch
 * @param {Function} ctx.commit
 * @since 1.1.0.
 * @category action
 * @author Tiago Martinho
 *
 */
const resetPassword = async ({
    rootState: { storePayload },
    commit
}) => {
    const { password, confirmPassword } = storePayload.payload;

    const formData = {
        ...( password && { password }),
        ...( confirmPassword && { password_confirmation: confirmPassword }),
        token: router.currentRoute.query.token,
        email: router.currentRoute.query.email
    };

    // valida se os quatros campos foram preenchidos. ( email, password, confirmPassword, token )
    // caso tenham sido, é submitido um pedido de API, de forma a resetar password
    //     caso a password seja resetada, é guardado a sua informação em store e redirecionado para a home
    //                                    ou para a página que tentou aceder
    //     caso não tenha, é apresentado uma mensagem de erro ao utilizador.
    // case não tenha sido, é apresentado uma mensagem de erro ao utilizador.
    if ( Object.keys( formData ).length === 4 )
        handlerRequestPassword({ commit, formData, method: 'post' });
    else {
        const missingFields = {
            ...( !formData.password && { password: true }),
            ...( !formData.password_confirmation && { confirmPassword: true })
        };

        commit( 'MANAGE_MISSING_FIELDS', missingFields );
    }
};

/**
 * [action]
 * Processa o forgot password
 * Realiza o pedido a API com as informações inseridas no formulário de forgot password ( email )
 *
 * @param {Object} ctx
 * @param {Object} ctx.storePayload
 * @param {Object} ctx.state
 * @param {Function} ctx.dispatch
 * @param {Function} ctx.commit
 * @since 1.1.0.
 * @category action
 * @author Tiago Martinho
 *
 */
const forgotPassword = async ({ rootState: { storePayload }, commit }) => {
    const formData = storePayload.payload;

    if ( formData.email )
        handlerRequestPassword({ commit, formData });
    else
        commit( 'MANAGE_MISSING_FIELDS', { email: true });
};

/**
 * [action]
 * Registo do utilizador & token na store
 *
 * @param {Object} ctx
 * @param {Function} ctx.commit
 * @param {Object} payload
 * @param {String} payload.accessToken
 * @param {String} payload.accessToken
 * @param {Object} payload.user
 * @since 1.1.0.
 * @category action
 * @author Tiago Martinho
 *
*/
const registUser = ({ commit }, { accessToken, tokenType, user }) => {
    // armazenado na store o toke & user
    commit( 'SAVE_TOKEN', accessToken );
    commit( 'REGIST_USER', user );
    commit( 'RESET_PAYLOAD' );
    commit( 'RESET_ALL_ERRORS' );

    // definição do token nos headers do axios
    axios.defaults.headers.common.Authorization = `${tokenType} ${accessToken}`;

    // redirect para a rota.
    const route = router.currentRoute.query.redirect || '/';
    setTimeout( () => {
        router.push( route );
    }, 300 );
};

/**
 * [action]
 * Logout do software.
 *
 * @param {Object} ctx
 * @param {Function} ctx.commit
 * @since 1.1.0.
 * @category action
 * @author Tiago Martinho
 *
 */
const logout = async ({ commit }) => {
    get( '/auth/logout' );
    commit( 'LOGOUT' );
    router.go({ name: 'login' });
};

export default {
    processLogin,
    requestResetPassword,
    resetPassword,
    forgotPassword,
    registUser,
    logout
};